import { createRouter, createWebHashHistory, RouteRecordRaw } from "vue-router";
import constants from "common/src/constants";
import Top from "@/views/SCR100.vue";
import Contract from "@/views/SCR120.vue";
import DisclosureStatement from "@/views/SCR131.vue";
import Confirm from "@/views/SCR132.vue";
import Renewal from "@/views/SCR130.vue";
import RenewalConfirm from "@/views/SCR140.vue";
import Payment from "@/views/PAY010.vue";
import RenewalSubmited from "@/views/SCR150.vue";
import Login from "@/views/SCR200.vue";
import Password from "@/views/SCR240.vue";
import PasswordChanged from "@/views/SCR250.vue";
import Inquiry from "@/views/SCR260.vue";
import Forbidden from "common/src/views/error-pages/403.vue";
import MethodNotAllowed from "common/src/views/error-pages/405.vue";
import RequestTimeOut from "common/src/views/error-pages/408.vue";
import InternalServerError from "common/src/views/error-pages/500.vue";
import ServiceUnavailable from "common/src/views/error-pages/503.vue";
import NotFound from "common/src/views/error-pages/404.vue";
import { MutationTypes, useStore } from "@/store";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Login",
    component: Login,
    meta: { title: "ログイン", anonymous: true, unneededPassChanged: true },
  },
  {
    path: "/top",
    name: "Top",
    component: Top,
    meta: { title: "契約一覧" },
  },
  {
    path: "/contract",
    name: "Contract",
    component: Contract,
    meta: { title: "契約照会" },
  },
  {
    path: "/disclosureStatement",
    name: "DisclosureStatement",
    component: DisclosureStatement,
    meta: { title: "重要事項説明、意向確認", isRenewal: true },
  },
  {
    path: "/confirm",
    name: "Confirm",
    component: Confirm,
    meta: { title: "確認メール送信結果", isRenewal: true },
  },
  {
    path: "/renewal",
    name: "Renewal",
    component: Renewal,
    meta: { title: "更新内容入力", isRenewal: true },
  },
  {
    path: "/renewal/confirm",
    name: "RenewalConfirm",
    component: RenewalConfirm,
    meta: { title: "更新内容確認", isRenewal: true },
  },
  {
    path: "/renewal/payment",
    name: "Payment",
    component: Payment,
    meta: { title: "コンビニ決済", isRenewal: true },
  },
  {
    path: "/renewal/submited",
    name: "RenewalSubmited",
    component: RenewalSubmited,
    meta: { title: "更新手続き完了", isRenewal: true },
  },
  {
    path: "/password",
    name: "Password",
    component: Password,
    meta: { title: "パスワード変更", unneededPassChanged: true },
  },
  {
    path: "/password/changed",
    name: "PasswordChanged",
    component: PasswordChanged,
    meta: { title: "パスワード変更" },
  },
  {
    path: "/inquiry",
    name: "Inquiry",
    component: Inquiry,
    meta: { title: "お問い合わせ", unneededPassChanged: true },
  },
  {
    path: "/403",
    name: "403",
    component: Forbidden,
    meta: { errorPage: true, anonymous: true, unneededPassChanged: true },
  },
  {
    path: "/405",
    name: "405",
    component: MethodNotAllowed,
    meta: { errorPage: true, anonymous: true, unneededPassChanged: true },
  },
  {
    path: "/408",
    name: "408",
    component: RequestTimeOut,
    meta: { errorPage: true, anonymous: true, unneededPassChanged: true },
  },
  {
    path: "/500",
    name: "500",
    component: InternalServerError,
    meta: { errorPage: true, anonymous: true, unneededPassChanged: true },
  },
  {
    path: "/503",
    name: "503",
    component: ServiceUnavailable,
    meta: { errorPage: true, anonymous: true, unneededPassChanged: true },
  },
  {
    // 404ページ設定
    path: "/:pathMatch(.*)*",
    name: "404",
    component: NotFound,
    meta: { errorPage: true, anonymous: true, unneededPassChanged: true },
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const store = useStore();

  // 認証が必要なページか
  if (!to.meta.anonymous) {
    // 認証が必要なページの場合、トークン未セット時はログインページへ遷移させる
    if (!window.sessionStorage.getItem(constants.ACCESS_TOKEN_KEY_MYPAGE) || !window.sessionStorage.getItem(constants.REFRESH_TOKEN_KEY_MYPAGE)) {
      next({ name: "Login" });
      return;
    }
  }

  // ログイン済みの状態でログインページを表示しようとした場合はトップページに遷移
  if (to.name === "Login") {
    if (window.sessionStorage.getItem(constants.ACCESS_TOKEN_KEY_MYPAGE) && window.sessionStorage.getItem(constants.REFRESH_TOKEN_KEY_MYPAGE)) {
      next({ name: "Top" });
      return;
    }
  }

  // パスワード変更が必要なページか
  if (!to.meta.unneededPassChanged) {
    // パスワード変更が必要なページの場合、認証者情報のパスワード変更が必要がtrueの場合、パスワード変更画面へ遷移させる
    if (store.state.me?.needPasswordChange) {
      next({ name: "Password" });
      return;
    }
  }

  // 更新画面・エラー画面以外の画面の場合、ストアの更新情報を削除
  if (!to.meta.isRenewal && !to.meta.errorPage) {
    store.commit(MutationTypes.RESET_RENEWAL);
  }
  // 初期画面以外の更新系画面で、ストアへのセットがない場合は重要事項説明画面へ(遷移不要時は重要事項説明画面にてリダイレクトさせる)
  if (to.name !== "DisclosureStatement" && to.meta.isRenewal) {
    if (store.state.needDisclosure === undefined || store.state.needDisclosure) {
      next({ name: "DisclosureStatement" });
      return;
    }
  }

  // メールアドレス変更画面・エラー画面以外の画面の場合、ストアのメール情報を削除
  if (!to.meta.isMail && !to.meta.errorPage) {
    store.commit(MutationTypes.SET_SERIAL, "");
  }
  // 初期画面以外の更新メールアドレス変更系画面で、ストアへのセットがない場合はメールアドレス変更の初期画面へ
  if (to.name !== "MailAddress" && to.meta.isMail) {
    if (store.state.serialNo === "") {
      next({ name: "MailAddress" });
      return;
    }
  }

  next();
});

router.afterEach((to) => {
  // サイドバーによる遷移時にサイドバーを閉じるための処理
  (document.activeElement as HTMLLIElement)?.blur();
  if (to.meta.title) {
    document.title = `${to.meta.title} - ${process.env.VUE_APP_BASE_TITLE}`;
  } else {
    document.title = process.env.VUE_APP_BASE_TITLE;
  }
});

export default router;
